'use client';

import React, { memo } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';
import { useTheme } from '@/contexts/ThemeContext';

interface AppLogoProps {
  src?: string;
  lightSrc?: string;
  darkSrc?: string;
  iconName?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
}

const AppLogo = memo(function AppLogo({
  src = '/assets/images/app_logo.png',
  lightSrc,
  darkSrc,
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
}: AppLogoProps) {
  const { theme } = useTheme();

  // Directly compute the logo src based on current theme — no memoization
  // so every theme change triggers a fresh render with the correct image
  let resolvedSrc: string;
  if (theme === 'dark') {
    resolvedSrc = darkSrc ?? src;
  } else {
    resolvedSrc = lightSrc ?? src;
  }

  const containerClassName = [
    'flex items-center',
    onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClassName} onClick={onClick}>
      {resolvedSrc ? (
        // key={theme} forces React to unmount/remount the image element
        // whenever the theme changes, guaranteeing the new src is loaded
        <AppImage
          key={theme}
          src={resolvedSrc}
          alt="KCF Logo"
          width={size}
          height={size}
          className="flex-shrink-0"
          priority={true}
          unoptimized={resolvedSrc.endsWith('.svg')}
        />
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
